import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useState,
} from "react";
import {
  LayoutState,
  LocalizationState,
  UserState,
  initialLayoutState,
  initialLocalizationState,
  initialUserState,
  CaptionsState,
  initialCaptionsState,
  MeetingState,
  initialMeetingState,
  EventViewerState,
  initialEventViewerState,
  EmojiData,
  RedirectAfterLoginState,
  initialRedirectAfterLoginState,
  DisplayMessage,
} from "../types";
import { usePersistentUserChoices } from "@livekit/components-react";
import { STUB_USER } from "../stubs/stub-data";

interface IGlobalContext {
  user: UserState;
  setUser: Dispatch<SetStateAction<UserState>>;
  layout: LayoutState;
  setLayout: Dispatch<SetStateAction<LayoutState>>;
  localization: LocalizationState;
  setLocalization: Dispatch<SetStateAction<LocalizationState>>;
  captions: CaptionsState;
  setCaptions: Dispatch<SetStateAction<CaptionsState>>;
  meeting: MeetingState;
  setMeeting: Dispatch<SetStateAction<MeetingState>>;
  eventViewer: EventViewerState;
  setEventViewer: Dispatch<SetStateAction<EventViewerState>>;
  audioEnabled: boolean;
  setAudioEnabled: Dispatch<SetStateAction<boolean>>;
  audioDeviceId: string;
  setAudioDeviceId: Dispatch<SetStateAction<string>>;
  videoEnabled: boolean;
  setVideoEnabled: Dispatch<SetStateAction<boolean>>;
  videoDeviceId: string;
  setVideoDeviceId: Dispatch<SetStateAction<string>>;
  emojis: EmojiData[];
  setEmojis: Dispatch<SetStateAction<EmojiData[]>>;
  redirectAfterLogin: RedirectAfterLoginState;
  setRedirectAfterLogin: Dispatch<SetStateAction<RedirectAfterLoginState>>;
  messages: DisplayMessage[];
  setMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>;
}

export const GlobalContext = createContext<IGlobalContext>(
  {} as IGlobalContext
);

function GlobalContextProvider(props: { children: ReactNode }) {
  const stubbedUserState: UserState = import.meta.env.VITE_STUB
    ? {
        ...initialUserState,
        auth: STUB_USER,
        metadata: {
          ...initialUserState.metadata,
          name: STUB_USER.name,
        },
      }
    : initialUserState;

  const [user, setUser] = useState<UserState>(stubbedUserState);
  const [layout, setLayout] = useState<LayoutState>(initialLayoutState);
  const [localization, setLocalization] = useState<LocalizationState>(
    initialLocalizationState
  );
  const [captions, setCaptions] = useState<CaptionsState>(initialCaptionsState);
  const [meeting, setMeeting] = useState<MeetingState>(initialMeetingState);
  const [eventViewer, setEventViewer] = useState<EventViewerState>(
    initialEventViewerState
  );
  const [emojis, setEmojis] = useState<EmojiData[]>([]);

  const { userChoices: initialUserChoices } = usePersistentUserChoices();

  const [audioEnabled, setAudioEnabled] = useState(true);

  const [audioDeviceId, setAudioDeviceId] = useState(
    initialUserChoices.audioDeviceId
  );

  const [videoEnabled, setVideoEnabled] = useState(true);

  const [videoDeviceId, setVideoDeviceId] = useState(
    initialUserChoices.videoDeviceId
  );
  const [messages, setMessages] = useState<DisplayMessage[]>([]);

  const [redirectAfterLogin, setRedirectAfterLogin] = useState(
    initialRedirectAfterLoginState
  );

  return (
    <GlobalContext.Provider
      value={{
        user,
        setUser,
        layout,
        setLayout,
        localization,
        setLocalization,
        captions,
        setCaptions,
        meeting,
        setMeeting,
        eventViewer,
        setEventViewer,
        audioEnabled,
        setAudioEnabled,
        audioDeviceId,
        setAudioDeviceId,
        videoEnabled,
        setVideoEnabled,
        videoDeviceId,
        setVideoDeviceId,
        emojis,
        setEmojis,
        redirectAfterLogin,
        setRedirectAfterLogin,
        messages,
        setMessages,
      }}
    >
      {props.children}
    </GlobalContext.Provider>
  );
}

export default GlobalContextProvider;
